import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as groupsService from '@/services/groupsService'
import { useAuth } from '@/hooks/useAuth'
import { useLogAction } from '@/hooks/useLogAction'
import { GROUPS_KEY } from '@/constants/queryKeys'
import type { Group } from '@/types/models'

export function useGroups() {
  const { churchId } = useAuth()

  return useQuery({
    queryKey: [GROUPS_KEY, churchId],
    queryFn: () => groupsService.getGroups(churchId!),
    enabled: !!churchId,
  })
}

export function useGroup(id: string | undefined) {
  return useQuery({
    queryKey: [GROUPS_KEY, id],
    queryFn: () => groupsService.getGroup(id!),
    enabled: !!id,
  })
}

export function useAddGroup() {
  const queryClient = useQueryClient()
  const { churchId } = useAuth()
  const log = useLogAction()

  return useMutation({
    mutationFn: (
      data: Omit<Group, 'id' | 'churchId' | 'createdAt' | 'updatedAt'>,
    ) => groupsService.addGroup({ ...data, churchId: churchId! }),
    onSuccess: (groupId, variables) => {
      queryClient.invalidateQueries({ queryKey: [GROUPS_KEY] })
      log({
        action: 'group.create',
        entityType: 'group',
        entityId: groupId,
        details: { name: variables.name },
      })
    },
  })
}

export function useUpdateGroup() {
  const queryClient = useQueryClient()
  const log = useLogAction()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Partial<Omit<Group, 'id'>>
    }) => groupsService.updateGroup(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [GROUPS_KEY] })
      log({
        action: 'group.update',
        entityType: 'group',
        entityId: variables.id,
      })
    },
  })
}

export function useDeleteGroup() {
  const queryClient = useQueryClient()
  const log = useLogAction()

  return useMutation({
    mutationFn: (id: string) => groupsService.deleteGroup(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [GROUPS_KEY] })
      log({ action: 'group.delete', entityType: 'group', entityId: id })
    },
  })
}
